import { inject } from "fw";
import { Store, handle } from "fw-state";
import { LogoutAction, StartAction } from "./actions";
import { PlatformElasticsearchIndexInfo, PlatformElasticsearchInfo } from "models/platform";
import { PlatformRepository } from "network/platform-repository";
import { orderBy } from "lodash-es";

export type LoadingStatus = "loading" | "loaded" | "error";

interface ElasticsearchShape {
    elasticsearch: PlatformElasticsearchInfo;
    lastLoaded: Date;
    status: LoadingStatus;
}

export class ElasticsearchEnsureAction {}

export class ElasticsearchRefreshAction {}

@inject
export class ElasticsearchStore extends Store<ElasticsearchShape> {
    constructor(private platformRepo: PlatformRepository) {
        super();
    }

    defaultState(): ElasticsearchShape {
        return {
            elasticsearch: null,
            lastLoaded: null,
            status: null
        };
    }

    @handle(StartAction)
    private handleStartAction(action: StartAction) {
        this.setState(() => this.defaultState());
    }

    @handle(LogoutAction)
    private handleLogoutAction() {
        this.setState(() => this.defaultState());
    }

    @handle(ElasticsearchEnsureAction)
    private async handleEnsureAction(action: ElasticsearchEnsureAction) {
        const { lastLoaded, status } = this.state;
        if (status === "loaded") {
            // Reload the state store if the data is older than 5 minutes.
            const fiveMinutesAgo = new Date();
            fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);

            if (lastLoaded > fiveMinutesAgo) {
                return;
            }
        }

        this.setState((state) => ({
            ...state,
            status: "loading"
        }));

        await this.loadData();
    }

    @handle(ElasticsearchRefreshAction)
    private async handleRefreshAction() {
        if (this.state.status === "loading") {
            return;
        }

        this.setState((state) => ({
            ...state,
            status: "loading"
        }));

        await this.loadData();
    }

    private async loadData() {
        try {
            const elasticsearch = await this.platformRepo.elasticsearch();

            this.setState((state) => ({
                ...state,
                elasticsearch: elasticsearch,
                lastLoaded: new Date(),
                status: "loaded"
            }));
        } catch (err) {
            this.setState((state) => ({
                ...state,
                elasticsearch: null,
                lastLoaded: null,
                status: "error"
            }));
        }
    }
}
